import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  X, Check, AlertCircle, ArrowRight, ChevronDown, ChevronUp,
  CheckCircle, RotateCcw, XCircle, Calendar, Clock, Send, UserX, Flag,
  Upload, Image, FileText, DollarSign, MapPin, ToggleLeft, ToggleRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const ACTION_OPTIONS = [
  { value: "Completed", label: "Completed", icon: CheckCircle, route: "QA", color: "text-[#6B8F62]", bg: "bg-[#6B8F62]/10 border-[#6B8F62]/25" },
  { value: "No Show", label: "No Show", icon: UserX, route: "dynamic", color: "text-[#DC2626]", bg: "bg-[#DC2626]/10 border-[#DC2626]/25" },
  { value: "Follow Up", label: "Follow Up", icon: Flag, route: "Dispatch", color: "text-[#B08A3E]", bg: "bg-[#B08A3E]/10 border-[#B08A3E]/25" },
  { value: "Internal flipped to Exterior", label: "Internal flipped to Exterior", icon: ArrowRight, route: "QA", color: "text-[#7A6B8F]", bg: "bg-[#7A6B8F]/10 border-[#7A6B8F]/25" },
  { value: "Returned by Inspector", label: "Returned by Inspector", icon: RotateCcw, route: "Dispatch", color: "text-[#C07070]", bg: "bg-[#C07070]/10 border-[#C07070]/25" },
  { value: "Cancelled (Safeguard)", label: "Cancelled (Safeguard)", icon: XCircle, route: "Archive", color: "text-[#8B4545]", bg: "bg-[#8B4545]/10 border-[#8B4545]/20" },
  { value: "Rescheduled", label: "Rescheduled (Internal)", icon: Calendar, route: "Scheduling", color: "text-[#2F5D8A]", bg: "bg-[#2F5D8A]/10 border-[#2F5D8A]/25" },
  { value: "Transmission Next Day", label: "Transmission Next Day", icon: Clock, route: "Hold", color: "text-[#B08A3E]", bg: "bg-[#B08A3E]/10 border-[#B08A3E]/25" },
  { value: "Transmission in 2 Days", label: "Transmission in 2 Days", icon: Clock, route: "Hold", color: "text-[#9A7735]", bg: "bg-[#9A7735]/10 border-[#9A7735]/25" },
];

const ROUTE_LABELS = {
  QA: { label: "QA", cls: "bg-[#6B8F62]/15 text-[#6B8F62] border-[#6B8F62]/30" },
  Dispatch: { label: "Dispatch", cls: "bg-[#C07070]/15 text-[#C07070] border-[#C07070]/30" },
  "Follow Up": { label: "Follow Up", cls: "bg-[#B08A3E]/15 text-[#B08A3E] border-[#B08A3E]/30" },
  Flagged: { label: "Flagged", cls: "bg-[#9A7735]/10 text-[#9A7735] border-[#9A7735]/25" },
  Pending: { label: "Pending", cls: "bg-[#9A7735]/10 text-[#9A7735] border-[#9A7735]/25" },
  Archive: { label: "Archive", cls: "bg-gray-100 text-gray-500 border-gray-300" },
  Scheduling: { label: "Scheduling", cls: "bg-[#2F5D8A]/10 text-[#2F5D8A] border-[#2F5D8A]/25" },
  Reschedule: { label: "Reschedule", cls: "bg-[#2F5D8A]/10 text-[#2F5D8A] border-[#2F5D8A]/25" },
  Hold: { label: "Hold", cls: "bg-[#B08A3E]/10 text-[#B08A3E] border-[#B08A3E]/25" },
};

const getNoShowRoute = (flowType) => {
  if (flowType === "Internal") return "Reschedule";
  return "Follow Up";
};

const CloseWithActionsModal = ({ orders, inspectorName, onClose, onSuccess, hideOfficeOnly = false }) => {
  const [orderActions, setOrderActions] = useState({});
  const [processing, setProcessing] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [bulkAction, setBulkAction] = useState("");
  const [routeDate, setRouteDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });

  // NEW: Closing attachments & day rate
  const [whatsappProofUrl, setWhatsappProofUrl] = useState("");
  const [whatsappProofName, setWhatsappProofName] = useState("");
  const [routeFileUrl, setRouteFileUrl] = useState("");
  const [routeFileName, setRouteFileName] = useState("");
  const [systemRoute, setSystemRoute] = useState(null);
  const [dayRateEnabled, setDayRateEnabled] = useState(false);
  const [dayRateAmount, setDayRateAmount] = useState("");
  const [uploading, setUploading] = useState("");

  // Daily Close Notes (Angela 2026-05-09) — append-only audit notes.
  // Stored separately in db.daily_close_notes; never mutate order status.
  const [notes, setNotes] = useState([]);
  const [dayCommentDraft, setDayCommentDraft] = useState("");
  const [orderSearch, setOrderSearch] = useState("");
  const [selectedNoteOrder, setSelectedNoteOrder] = useState(null);
  const [orderCommentDraft, setOrderCommentDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const reloadNotes = useCallback(async () => {
    if (!inspectorName || !routeDate) return;
    try {
      const r = await axios.get(`${API}/dispatch/close-notes`, {
        params: { inspector: inspectorName, route_date: routeDate },
      });
      setNotes(r.data.notes || []);
    } catch { /* ignore — notes are optional */ }
  }, [inspectorName, routeDate]);

  useEffect(() => { reloadNotes(); }, [reloadNotes]);

  const saveNote = async (scope, comment, order_number = null) => {
    if (!comment.trim()) return;
    setSavingNote(true);
    try {
      await axios.post(`${API}/dispatch/close-notes`, {
        inspector_name: inspectorName, route_date: routeDate,
        comment, scope, order_number,
      });
      if (scope === "day") setDayCommentDraft("");
      if (scope === "order") { setOrderCommentDraft(""); setSelectedNoteOrder(null); }
      await reloadNotes();
    } catch (e) {
      alert(`Failed to save note: ${e?.response?.data?.detail || e.message}`);
    } finally { setSavingNote(false); }
  };

  const deleteNote = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    try { await axios.delete(`${API}/dispatch/close-notes/${id}`); await reloadNotes(); }
    catch (e) { alert(`Failed: ${e?.response?.data?.detail || e.message}`); }
  };

  const filteredOrdersForNote = orderSearch.trim()
    ? orders.filter(o => String(o.order_number || "").toLowerCase().includes(orderSearch.trim().toLowerCase()))
    : orders.slice(0, 8);

  const orderFlowTypes = {};
  orders.forEach(o => { orderFlowTypes[o.id] = o.flow_type || "Regular"; });

  // Check if system route exists for this inspector+date
  const checkSystemRoute = useCallback(async (date) => {
    if (!inspectorName || !date) return;
    try {
      const res = await axios.get(`${API}/dispatch/inspector-route`, { params: { inspector: inspectorName, route_date: date } });
      setSystemRoute(res.data.exists ? res.data.route : null);
    } catch { setSystemRoute(null); }
  }, [inspectorName]);

  useEffect(() => {
    const initial = {};
    orders.forEach(o => {
      initial[o.id] = { action: "Completed", notes: "", return_reason: "", transmission_date: "", send_email: false, follow_up_reason: "", extra_pay: "", extra_pay_reason: "" };
    });
    setOrderActions(initial);
  }, [orders]);

  useEffect(() => { checkSystemRoute(routeDate); }, [routeDate, checkSystemRoute]);

  const updateOrderField = (orderId, field, value) => {
    setOrderActions(prev => ({ ...prev, [orderId]: { ...prev[orderId], [field]: value } }));
  };

  const applyBulkAction = (action) => {
    setBulkAction(action);
    setOrderActions(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(id => {
        next[id] = { ...next[id], action, notes: "", return_reason: "", transmission_date: "", send_email: false, follow_up_reason: "" };
      });
      return next;
    });
  };

  const getEffectiveRoute = (action, orderId) => {
    if (action === "No Show") return getNoShowRoute(orderFlowTypes[orderId]);
    if (action === "Follow Up") {
      const oa = orderActions[orderId];
      if (oa?.follow_up_reason === "Inspector Error") return "Follow Up";
      if (oa?.follow_up_reason === "Safeguard Error") return "Flagged";
      return "Follow Up";
    }
    const opt = ACTION_OPTIONS.find(a => a.value === action);
    return opt?.route || "QA";
  };

  const getRouteSummary = () => {
    const summary = {};
    Object.entries(orderActions).forEach(([orderId, oa]) => {
      const route = getEffectiveRoute(oa.action, orderId);
      summary[route] = (summary[route] || 0) + 1;
    });
    return summary;
  };

  const handleFileUpload = async (file, type) => {
    setUploading(type);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("attachment_type", type);
      const res = await axios.post(`${API}/dispatch/upload-closing-attachment`, fd);
      if (type === "whatsapp_proof") {
        setWhatsappProofUrl(res.data.url);
        setWhatsappProofName(res.data.filename);
      } else {
        setRouteFileUrl(res.data.url);
        setRouteFileName(res.data.filename);
      }
    } catch (e) {
      console.error("Upload failed:", e);
    } finally {
      setUploading("");
    }
  };

  const isValid = () => {
    if (!routeDate) return false;
    if (dayRateEnabled && (!dayRateAmount || parseFloat(dayRateAmount) <= 0)) return false;
    for (const [, oa] of Object.entries(orderActions)) {
      if (oa.action === "Returned by Inspector" && !oa.return_reason.trim()) return false;
      if (oa.action === "Internal flipped to Exterior" && !oa.notes.trim()) return false;
      if (oa.action === "No Show" && !oa.notes.trim()) return false;
      if (oa.action === "Follow Up" && !oa.follow_up_reason) return false;
      if (oa.extra_pay && parseFloat(oa.extra_pay) > 0 && !oa.extra_pay_reason.trim()) return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!isValid()) return;
    setProcessing(true);
    const payload = {
      inspector_name: inspectorName,
      route_date: routeDate,
      day_rate_enabled: dayRateEnabled,
      day_rate_amount: dayRateEnabled ? parseFloat(dayRateAmount) : 0,
      whatsapp_proof_url: whatsappProofUrl,
      route_file_url: routeFileUrl,
      system_route_url: systemRoute?.gpx_url || "",
      orders: Object.entries(orderActions).map(([id, oa]) => ({
        order_id: id,
        action: oa.action,
        notes: oa.notes,
        return_reason: oa.return_reason,
        transmission_date: oa.transmission_date,
        send_email: oa.send_email,
        follow_up_reason: oa.follow_up_reason || "",
        extra_pay: oa.extra_pay ? parseFloat(oa.extra_pay) : 0,
        extra_pay_reason: oa.extra_pay_reason || "",
      })),
    };
    try {
      const res = await axios.post(`${API}/dispatch/close-with-actions`, payload);
      const data = res.data || {};
      const parts = [];
      if (data.to_qa > 0) parts.push(`${data.to_qa} -> QA`);
      if (data.to_dispatch > 0) parts.push(`${data.to_dispatch} -> Dispatch`);
      if (data.to_archive > 0) parts.push(`${data.to_archive} -> Archive`);
      if (data.to_scheduling > 0) parts.push(`${data.to_scheduling} -> Scheduling`);
      if (data.to_hold > 0) parts.push(`${data.to_hold} -> Hold`);
      if (data.to_follow_up > 0) parts.push(`${data.to_follow_up} Follow Up (payment zeroed)`);
      if (data.to_pending > 0) parts.push(`${data.to_pending} Flagged (Safeguard)`);
      if (data.no_show_count > 0) parts.push(`${data.no_show_count} No Show`);
      let msg;
      if ((data.total || 0) === 0 && (data.skipped_count || 0) > 0) {
        msg = `All ${data.skipped_count} selected order(s) were already closed for ${payload.inspector_name} on ${payload.route_date}. Nothing new to process.`;
      } else {
        msg = `${data.total || 0} order(s) processed${parts.length ? `: ${parts.join(", ")}` : ""}`;
        if (data.skipped_count > 0) msg += ` | ${data.skipped_count} skipped (already closed)`;
        if (data.appended_to_existing_batch) msg += ` | Appended to existing close batch`;
        if (dayRateEnabled) msg += ` | Day Rate: $${parseFloat(dayRateAmount).toFixed(2)}`;
      }
      onSuccess(msg);
    } catch (e) {
      const detail = e.response?.data?.detail;
      onSuccess(`Error: ${typeof detail === "string" ? detail : detail?.message || "Failed to process orders"}`);
    } finally {
      setProcessing(false);
    }
  };

  const summary = getRouteSummary();
  const validationOk = isValid();

  const getValidationMessage = () => {
    const msgs = [];
    if (dayRateEnabled && (!dayRateAmount || parseFloat(dayRateAmount) <= 0)) msgs.push("Day Rate requires a valid amount");
    const hasReturnMissing = Object.values(orderActions).some(oa => oa.action === "Returned by Inspector" && !oa.return_reason.trim());
    const hasNoShowMissing = Object.values(orderActions).some(oa => oa.action === "No Show" && !oa.notes.trim());
    const hasFlippedMissing = Object.values(orderActions).some(oa => oa.action === "Internal flipped to Exterior" && !oa.notes.trim());
    const hasFollowUpMissing = Object.values(orderActions).some(oa => oa.action === "Follow Up" && !oa.follow_up_reason);
    const hasExtraPayNoReason = Object.values(orderActions).some(oa => oa.extra_pay && parseFloat(oa.extra_pay) > 0 && !oa.extra_pay_reason.trim());
    if (hasReturnMissing) msgs.push('"Returned by Inspector" requires a return reason');
    if (hasNoShowMissing) msgs.push('"No Show" requires a mandatory comment');
    if (hasFlippedMissing) msgs.push('"Internal flipped to Exterior" requires notes');
    if (hasFollowUpMissing) msgs.push('"Follow Up" requires selecting a reason');
    if (hasExtraPayNoReason) msgs.push("Extra Pay requires a reason");
    return msgs.join(". ") + ".";
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="close-actions-modal">
      <div className="bg-white rounded-xl border border-gray-200 w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-[#1F2937]">Close Selected Orders</h3>
            <p className="text-xs text-gray-500">{orders.length} order{orders.length !== 1 ? "s" : ""} for {inspectorName}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400" data-testid="close-actions-modal-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable body — Angela 2026-05-11 mobile fix: wrap top section +
            bulk action + orders list into one flex-1 overflow-y-auto so the
            footer (Cancel + Close N Orders) stays pinned and the Bulk Action
            buttons (Completed/Refused/etc.) are always reachable on mobile. */}
        <div className="flex-1 overflow-y-auto min-h-0">
        {/* Route Date + Attachments Section */}
        <div className="px-5 py-3 border-b border-gray-100 bg-[#FDF8F0] space-y-3">
          {/* Row 1: Route Date */}
          <div className="flex items-center gap-3">
            <label className="text-[10px] font-semibold text-[#B08A3E] uppercase tracking-wider whitespace-nowrap">Route Date *</label>
            <input type="date" value={routeDate} onChange={e => setRouteDate(e.target.value)}
              className="px-3 py-1.5 bg-white border border-[#B08A3E]/30 rounded text-sm text-[#1F2937] font-medium focus:outline-none focus:ring-1 focus:ring-[#B08A3E]"
              data-testid="route-date-input" />
            <span className="text-[10px] text-gray-400">Date of execution</span>
          </div>

          {/* Row 2: Uploads + Day Rate — Office-only (hidden for Inspector self-close) */}
          {!hideOfficeOnly && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* WhatsApp Proof Upload */}
            <div className="bg-white rounded-lg border border-gray-200 p-2.5" data-testid="whatsapp-proof-section">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Image className="h-3.5 w-3.5 text-[#25D366]" />
                <span className="text-[10px] font-semibold text-gray-500 uppercase">WhatsApp Proof</span>
              </div>
              {whatsappProofUrl ? (
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-3.5 w-3.5 text-[#6B8F62]" />
                  <span className="text-xs text-[#6B8F62] truncate flex-1">{whatsappProofName}</span>
                  <a href={whatsappProofUrl} target="_blank" rel="noopener noreferrer"
                     className="text-[10px] font-semibold text-[#2F5D8A] hover:underline"
                     data-testid="whatsapp-proof-open">Open</a>
                  <button onClick={() => { setWhatsappProofUrl(""); setWhatsappProofName(""); }} className="text-gray-400 hover:text-[#C07070]"><X className="h-3 w-3" /></button>
                </div>
              ) : (
                <label className={`flex items-center gap-2 px-3 py-1.5 rounded border border-dashed cursor-pointer text-xs ${uploading === "whatsapp_proof" ? "border-[#25D366] bg-[#25D366]/5 text-[#25D366]" : "border-gray-300 text-gray-500 hover:border-[#25D366] hover:text-[#25D366]"}`}>
                  <Upload className="h-3.5 w-3.5" />
                  {uploading === "whatsapp_proof" ? "Uploading..." : "Upload image"}
                  <input type="file" accept="image/*" className="hidden" data-testid="whatsapp-proof-input"
                    onChange={e => e.target.files[0] && handleFileUpload(e.target.files[0], "whatsapp_proof")} />
                </label>
              )}
            </div>

            {/* Route File Upload */}
            <div className="bg-white rounded-lg border border-gray-200 p-2.5" data-testid="route-file-section">
              <div className="flex items-center gap-1.5 mb-1.5">
                <MapPin className="h-3.5 w-3.5 text-[#2F5D8A]" />
                <span className="text-[10px] font-semibold text-gray-500 uppercase">Route File</span>
              </div>
              {systemRoute && (
                <div className="flex items-center gap-2 mb-1.5 px-2 py-1 bg-[#2F5D8A]/5 rounded text-[10px] text-[#2F5D8A] border border-[#2F5D8A]/15">
                  <FileText className="h-3 w-3" />
                  <span className="truncate flex-1">Route from system: {systemRoute.filename}</span>
                  <CheckCircle className="h-3 w-3 text-[#6B8F62]" />
                </div>
              )}
              {routeFileUrl ? (
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-3.5 w-3.5 text-[#6B8F62]" />
                  <span className="text-xs text-[#6B8F62] truncate flex-1">{routeFileName}</span>
                  <a href={routeFileUrl} target="_blank" rel="noopener noreferrer"
                     className="text-[10px] font-semibold text-[#2F5D8A] hover:underline"
                     data-testid="route-file-open">Open</a>
                  <button onClick={() => { setRouteFileUrl(""); setRouteFileName(""); }} className="text-gray-400 hover:text-[#C07070]"><X className="h-3 w-3" /></button>
                </div>
              ) : (
                <label className={`flex items-center gap-2 px-3 py-1.5 rounded border border-dashed cursor-pointer text-xs ${uploading === "route_file" ? "border-[#2F5D8A] bg-[#2F5D8A]/5 text-[#2F5D8A]" : "border-gray-300 text-gray-500 hover:border-[#2F5D8A] hover:text-[#2F5D8A]"}`}>
                  <Upload className="h-3.5 w-3.5" />
                  {uploading === "route_file" ? "Uploading..." : "Upload GPX/PDF/IMG"}
                  <input type="file" accept=".gpx,.pdf,.png,.jpg,.jpeg,.webp" className="hidden" data-testid="route-file-input"
                    onChange={e => e.target.files[0] && handleFileUpload(e.target.files[0], "route_file")} />
                </label>
              )}
            </div>

            {/* Day Rate Toggle */}
            <div className={`rounded-lg border p-2.5 transition-colors ${dayRateEnabled ? "bg-[#D1A054]/5 border-[#D1A054]/40" : "bg-white border-gray-200"}`} data-testid="day-rate-section">
              <div className="flex items-center gap-1.5 mb-1.5">
                <DollarSign className="h-3.5 w-3.5 text-[#D1A054]" />
                <span className="text-[10px] font-semibold text-gray-500 uppercase">Day Rate</span>
              </div>
              <button onClick={() => setDayRateEnabled(!dayRateEnabled)}
                className={`flex items-center gap-2 text-xs font-medium transition-colors ${dayRateEnabled ? "text-[#D1A054]" : "text-gray-400"}`}
                data-testid="day-rate-toggle">
                {dayRateEnabled ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
                {dayRateEnabled ? "Route Closed (Day Rate)" : "Per-order rates"}
              </button>
              {dayRateEnabled && (
                <div className="mt-1.5">
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[#D1A054] text-xs font-bold">$</span>
                    <input type="number" step="0.01" min="0" value={dayRateAmount}
                      onChange={e => setDayRateAmount(e.target.value)}
                      placeholder="Day amount"
                      className="w-full pl-6 pr-2 py-1.5 bg-white border border-[#D1A054]/40 rounded text-xs text-[#1F2937] font-medium focus:outline-none focus:ring-1 focus:ring-[#D1A054]"
                      data-testid="day-rate-amount" />
                  </div>
                  <p className="text-[9px] text-[#D1A054] mt-1">Per-order payout zeroed. Day rate recorded.</p>
                </div>
              )}
            </div>
          </div>
          )}

          {/* Daily Close Notes (Angela 2026-05-09) — audit-only, never changes status */}
          <div className="bg-white rounded-lg border border-[#B08A3E]/30 p-3 mt-3" data-testid="close-notes-section">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="h-3.5 w-3.5 text-[#B08A3E]" />
              <span className="text-[11px] font-bold text-[#B08A3E] uppercase tracking-wider">Daily Close Notes / Inspector Day Comments</span>
              <span className="text-[10px] text-gray-400 ml-auto">Adding notes does NOT change order status</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Day-level comment */}
              <div className="bg-gray-50 rounded p-2.5 border border-gray-200">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Day Comment</p>
                <textarea value={dayCommentDraft} onChange={e => setDayCommentDraft(e.target.value)}
                  rows={2} placeholder="e.g. Inspector had route issue today"
                  className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-[#B08A3E]"
                  data-testid="day-comment-textarea" />
                <button onClick={() => saveNote("day", dayCommentDraft)}
                  disabled={!dayCommentDraft.trim() || savingNote}
                  className="mt-1 px-3 py-1 bg-[#B08A3E] text-white text-[10px] font-semibold rounded disabled:opacity-40"
                  data-testid="day-comment-add">Add Day Comment</button>
              </div>

              {/* Order-specific comment */}
              <div className="bg-gray-50 rounded p-2.5 border border-gray-200">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Order Comment</p>
                <input value={orderSearch} onChange={e => setOrderSearch(e.target.value)}
                  placeholder="Search order number…"
                  className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-[#B08A3E] mb-1"
                  data-testid="order-search-input" />
                <div className="flex flex-wrap gap-1 max-h-16 overflow-y-auto" data-testid="order-search-results">
                  {filteredOrdersForNote.map(o => (
                    <button key={o.id} onClick={() => setSelectedNoteOrder(o)}
                      className={`px-1.5 py-0.5 rounded text-[10px] font-mono border ${selectedNoteOrder?.id === o.id ? "bg-[#B08A3E] text-white border-[#B08A3E]" : "bg-white text-gray-600 border-gray-200 hover:border-[#B08A3E]"}`}
                      data-testid={`order-search-pick-${o.order_number}`}>{o.order_number}</button>
                  ))}
                </div>
                {selectedNoteOrder && (
                  <div className="mt-1.5">
                    <p className="text-[9px] text-[#B08A3E]">For order <span className="font-bold">{selectedNoteOrder.order_number}</span>:</p>
                    <textarea value={orderCommentDraft} onChange={e => setOrderCommentDraft(e.target.value)}
                      rows={2} placeholder="e.g. Owner refused inspection"
                      className="w-full mt-0.5 px-2 py-1.5 bg-white border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-[#B08A3E]"
                      data-testid="order-comment-textarea" />
                    <button onClick={() => saveNote("order", orderCommentDraft, selectedNoteOrder.order_number)}
                      disabled={!orderCommentDraft.trim() || savingNote}
                      className="mt-1 px-3 py-1 bg-[#B08A3E] text-white text-[10px] font-semibold rounded disabled:opacity-40"
                      data-testid="order-comment-add">Add Order Comment</button>
                  </div>
                )}
              </div>
            </div>

            {notes.length > 0 && (
              <div className="mt-2 border-t border-gray-100 pt-2 max-h-40 overflow-y-auto" data-testid="close-notes-list">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Notes for this close ({notes.length})</p>
                <ul className="space-y-1">
                  {notes.map(n => (
                    <li key={n.id} className="bg-gray-50 rounded p-2 text-[11px] flex items-start gap-2 group" data-testid={`close-note-item-${n.id}`}>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${n.scope === "day" ? "bg-[#2F5D8A]/15 text-[#2F5D8A]" : "bg-[#B08A3E]/15 text-[#B08A3E]"}`}>
                        {n.scope === "day" ? "DAY" : `ORDER ${n.order_number}`}
                      </span>
                      <span className="flex-1 text-gray-700">{n.comment}</span>
                      <span className="text-[9px] text-gray-400">{(n.created_by || "").split("@")[0]} · {(n.created_at || "").slice(11, 16)}</span>
                      <button onClick={() => deleteNote(n.id)} className="text-gray-300 hover:text-[#C07070] opacity-0 group-hover:opacity-100" data-testid={`close-note-del-${n.id}`}><X className="h-3 w-3" /></button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Bulk Action */}
        <div className="px-5 py-2.5 border-b border-gray-50 bg-gray-50/50">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Set All:</span>
            {ACTION_OPTIONS.map(opt => (
              <button key={opt.value} onClick={() => applyBulkAction(opt.value)}
                className={`px-2.5 py-1 rounded text-[10px] font-semibold border transition-colors ${
                  bulkAction === opt.value ? opt.bg + " ring-1 ring-current" : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"
                }`}
                data-testid={`bulk-action-${opt.value.toLowerCase().replace(/\s+/g, "-").replace(/[()]/g, "")}`}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Orders List — scroll handled by parent body wrapper */}
        <div className="px-5 py-3 space-y-2">
          {orders.map(order => {
            const oa = orderActions[order.id] || { action: "Completed", notes: "", return_reason: "", transmission_date: "", send_email: false, extra_pay: "", extra_pay_reason: "" };
            const actionOpt = ACTION_OPTIONS.find(a => a.value === oa.action);
            const isExpanded = expandedOrder === order.id;
            const needsReason = oa.action === "Returned by Inspector" && !oa.return_reason.trim();
            const needsNoShowComment = oa.action === "No Show" && !oa.notes.trim();
            const needsFlippedNotes = oa.action === "Internal flipped to Exterior" && !oa.notes.trim();
            const needsFollowUpReason = oa.action === "Follow Up" && !oa.follow_up_reason;
            const isNoShow = oa.action === "No Show";
            const isFollowUp = oa.action === "Follow Up";
            const effectiveRoute = getEffectiveRoute(oa.action, order.id);
            const routeLabel = ROUTE_LABELS[effectiveRoute] || ROUTE_LABELS["QA"];
            const flowType = orderFlowTypes[order.id];
            const hasExtraPay = oa.extra_pay && parseFloat(oa.extra_pay) > 0;

            return (
              <div key={order.id} className={`border rounded-lg overflow-hidden transition-colors ${
                needsReason || needsNoShowComment || needsFlippedNotes || needsFollowUpReason ? "border-[#C07070]/40" : isNoShow ? "border-[#DC2626]/30" : hasExtraPay ? "border-[#D1A054]/40" : "border-gray-200"
              }`} data-testid={`close-order-${order.id}`}>
                {/* Order Row */}
                <div className={`flex items-center gap-2 px-3 py-2 ${isNoShow ? "bg-[#DC2626]/[0.03]" : "bg-white"}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-[#1F2937] whitespace-nowrap">{order.order_number}</span>
                      {isNoShow && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#DC2626] text-white tracking-wider" data-testid={`no-show-tag-${order.id}`}>NO SHOW</span>}
                      {hasExtraPay && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#D1A054] text-white">+${parseFloat(oa.extra_pay).toFixed(0)}</span>}
                      <span className="text-[11px] text-gray-500 truncate" title={`${order.address || ""}${order.city ? `, ${order.city}` : ""}${order.state ? ` ${order.state}` : ""}`}>
                        {order.address}{order.city ? `, ${order.city}` : ""}{order.state ? ` ${order.state}` : ""}
                      </span>
                      {isNoShow && <span className="text-[9px] text-gray-400 font-medium whitespace-nowrap">({flowType})</span>}
                    </div>
                  </div>

                  <select value={oa.action} onChange={e => updateOrderField(order.id, "action", e.target.value)}
                    className={`px-2 py-1 rounded text-xs font-semibold border focus:outline-none focus:ring-1 focus:ring-[#B08A3E] min-w-[180px] ${actionOpt?.bg || "bg-gray-100 border-gray-200"}`}
                    data-testid={`action-select-${order.id}`}>
                    {ACTION_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>

                  <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border whitespace-nowrap ${routeLabel.cls}`}>
                    <ArrowRight className="h-2.5 w-2.5 inline mr-0.5" />{routeLabel.label}
                  </span>

                  <button onClick={() => setExpandedOrder(isExpanded ? null : order.id)} className="p-1 rounded hover:bg-gray-100 text-gray-400" data-testid={`expand-order-${order.id}`}>
                    {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </button>
                </div>

                {/* Dynamic Fields */}
                {(isExpanded || needsReason || needsNoShowComment || needsFlippedNotes || needsFollowUpReason) && (
                  <div className={`px-3 py-2.5 border-t border-gray-100 space-y-2 ${isNoShow ? "bg-[#DC2626]/[0.02]" : isFollowUp ? "bg-[#B08A3E]/[0.02]" : "bg-gray-50/80"}`}>
                    {/* Follow Up */}
                    {oa.action === "Follow Up" && (
                      <div>
                        <label className={`block text-[9px] font-semibold uppercase tracking-wider mb-1 ${!oa.follow_up_reason ? "text-[#C07070]" : "text-[#B08A3E]"}`}>Follow Up Reason * (Required)</label>
                        <div className="flex gap-2">
                          {[{ value: "Inspector Error", label: "Inspector Error", desc: "Payment zeroed, wait for new closing", cls: "bg-[#C07070]" },
                            { value: "Safeguard Error", label: "Safeguard Error", desc: "Flag only, payment unchanged", cls: "bg-[#9A7735]" }].map(opt => (
                            <button key={opt.value} onClick={() => updateOrderField(order.id, "follow_up_reason", opt.value)}
                              className={`flex-1 px-3 py-2 rounded-lg border text-xs font-semibold transition-colors text-left ${oa.follow_up_reason === opt.value ? `${opt.cls} text-white border-transparent` : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"}`}
                              data-testid={`follow-up-reason-${order.id}-${opt.value.toLowerCase().replace(/\s/g, "-")}`}>
                              <div>{opt.label}</div>
                              <div className={`text-[9px] mt-0.5 ${oa.follow_up_reason === opt.value ? "text-white/80" : "text-gray-400"}`}>{opt.desc}</div>
                            </button>
                          ))}
                        </div>
                        {oa.follow_up_reason && (
                          <div className="flex items-center gap-3 mt-1.5 text-[9px] text-gray-400">
                            {oa.follow_up_reason === "Inspector Error"
                              ? <span>Status: <strong className="text-[#C07070]">Follow Up</strong> | Payment: <strong className="text-[#C07070]">$0 (zeroed)</strong> | Awaiting new closing</span>
                              : <span>Status: <strong className="text-[#9A7735]">Unchanged</strong> | Payment: <strong className="text-[#6B8F62]">Unchanged</strong> | Flagged for office</span>}
                          </div>
                        )}
                      </div>
                    )}

                    {/* No Show */}
                    {oa.action === "No Show" && (
                      <div>
                        <label className={`block text-[9px] font-semibold uppercase tracking-wider mb-0.5 ${!oa.notes.trim() ? "text-[#DC2626]" : "text-[#DC2626]/70"}`}>No Show Comment * (Required)</label>
                        <input value={oa.notes} onChange={e => updateOrderField(order.id, "notes", e.target.value)} placeholder="Describe the no show situation..."
                          className={`w-full px-2 py-1.5 bg-white border rounded text-xs focus:outline-none focus:ring-1 focus:ring-[#DC2626] ${!oa.notes.trim() ? "border-[#DC2626]" : "border-gray-200"}`}
                          data-testid={`no-show-comment-${order.id}`} />
                        <div className="flex items-center gap-3 mt-1.5 text-[9px] text-gray-400">
                          <span>Route: <strong className="text-[#1F2937]">{flowType === "Internal" ? "Scheduling (Reschedule)" : "Dispatch (Follow Up)"}</strong></span>
                          <span>Billable: <strong className="text-[#DC2626]">NO</strong></span>
                        </div>
                      </div>
                    )}

                    {/* Internal flipped to Exterior */}
                    {oa.action === "Internal flipped to Exterior" && (
                      <div>
                        <label className={`block text-[9px] font-semibold uppercase tracking-wider mb-0.5 ${!oa.notes.trim() ? "text-[#C07070]" : "text-[#7A6B8F]"}`}>Notes * (Required for flipped orders)</label>
                        <input value={oa.notes} onChange={e => updateOrderField(order.id, "notes", e.target.value)} placeholder="Explain why this internal order was completed as exterior..."
                          className={`w-full px-2 py-1.5 bg-white border rounded text-xs focus:outline-none focus:ring-1 focus:ring-[#7A6B8F] ${!oa.notes.trim() ? "border-[#C07070]" : "border-gray-200"}`}
                          data-testid={`flipped-notes-${order.id}`} />
                      </div>
                    )}

                    {/* Returned by Inspector */}
                    {oa.action === "Returned by Inspector" && (
                      <div>
                        <label className="block text-[9px] font-semibold text-[#C07070] uppercase tracking-wider mb-0.5">Return Reason *</label>
                        <select value={oa.return_reason} onChange={e => updateOrderField(order.id, "return_reason", e.target.value)}
                          className={`w-full px-2 py-1.5 bg-white border rounded text-xs focus:outline-none focus:ring-1 focus:ring-[#C07070] ${needsReason ? "border-[#C07070]" : "border-gray-200"}`}
                          data-testid={`return-reason-${order.id}`}>
                          <option value="">Select reason...</option>
                          <option value="No time">No time</option><option value="No access">No access</option>
                          <option value="Refused">Refused</option><option value="Wrong address">Wrong address</option>
                          <option value="Unsafe location">Unsafe location</option><option value="Weather">Weather</option><option value="Other">Other</option>
                        </select>
                      </div>
                    )}

                    {/* Rescheduled */}
                    {oa.action === "Rescheduled" && (
                      <div className="flex items-center gap-3 py-1">
                        <label className="text-[9px] font-semibold text-[#2F5D8A] uppercase tracking-wider">Send email to Safeguard?</label>
                        <div className="flex gap-2">
                          {[true, false].map(val => (
                            <button key={String(val)} onClick={() => updateOrderField(order.id, "send_email", val)}
                              className={`px-3 py-1 rounded text-xs font-semibold border transition-colors ${oa.send_email === val ? (val ? "bg-[#2F5D8A] text-white border-[#2F5D8A]" : "bg-gray-600 text-white border-gray-600") : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"}`}
                              data-testid={`send-email-${order.id}-${val}`}>{val ? "Yes" : "No"}</button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Transmission date */}
                    {(oa.action === "Transmission Next Day" || oa.action === "Transmission in 2 Days") && (
                      <div>
                        <label className="block text-[9px] font-semibold text-[#B08A3E] uppercase tracking-wider mb-0.5">Transmission Date (optional)</label>
                        <input type="date" value={oa.transmission_date} onChange={e => updateOrderField(order.id, "transmission_date", e.target.value)}
                          className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-[#B08A3E]"
                          data-testid={`transmission-date-${order.id}`} />
                      </div>
                    )}

                    {/* Notes (generic) */}
                    {oa.action !== "No Show" && oa.action !== "Internal flipped to Exterior" && (
                      <div>
                        <label className="block text-[9px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Notes</label>
                        <input value={oa.notes} onChange={e => updateOrderField(order.id, "notes", e.target.value)} placeholder="Optional notes..."
                          className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-gray-300"
                          data-testid={`close-notes-${order.id}`} />
                      </div>
                    )}

                    {/* Extra Pay / Special Pay — Office-only */}
                    {!hideOfficeOnly && (
                    <div className="border-t border-gray-100 pt-2">
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-3 w-3 text-[#D1A054]" />
                        <label className="text-[9px] font-semibold text-[#D1A054] uppercase tracking-wider">Extra Pay (optional)</label>
                      </div>
                      <div className="flex gap-2 mt-1">
                        <div className="relative w-24">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[#D1A054] text-[10px] font-bold">$</span>
                          <input type="number" step="0.01" min="0" value={oa.extra_pay} onChange={e => updateOrderField(order.id, "extra_pay", e.target.value)}
                            placeholder="0.00"
                            className="w-full pl-5 pr-2 py-1 bg-white border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-[#D1A054]"
                            data-testid={`extra-pay-${order.id}`} />
                        </div>
                        <input value={oa.extra_pay_reason} onChange={e => updateOrderField(order.id, "extra_pay_reason", e.target.value)}
                          placeholder="Reason (e.g., out-of-route urgent)"
                          className="flex-1 px-2 py-1 bg-white border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-[#D1A054]"
                          data-testid={`extra-pay-reason-${order.id}`} />
                      </div>
                    </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </div>
        {/* /scrollable body wrapper (mobile fix) */}

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-3 mb-3 flex-wrap" data-testid="close-route-summary">
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Routing:</span>
            {Object.entries(summary).filter(([, count]) => count > 0).map(([dest, count]) => (
              <span key={dest} className={`px-2 py-0.5 rounded text-[10px] font-bold border ${ROUTE_LABELS[dest]?.cls || "bg-gray-100 text-gray-500 border-gray-300"}`}>
                {count} {"->"} {ROUTE_LABELS[dest]?.label || dest}
              </span>
            ))}
            {dayRateEnabled && dayRateAmount && (
              <span className="px-2 py-0.5 rounded text-[10px] font-bold border bg-[#D1A054]/10 text-[#D1A054] border-[#D1A054]/30">Day Rate: ${parseFloat(dayRateAmount).toFixed(2)}</span>
            )}
          </div>

          {!validationOk && (
            <div className="flex items-center gap-2 mb-3 text-[#DC2626] text-xs" data-testid="close-validation-error">
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" /><span>{getValidationMessage()}</span>
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium rounded-lg transition-colors" data-testid="close-actions-cancel">Cancel</button>
            <button onClick={handleSubmit} disabled={processing || !validationOk}
              className="flex-1 px-4 py-2.5 bg-[#6B8F62] hover:bg-[#5A7D53] text-white text-sm font-semibold rounded-lg disabled:opacity-40 flex items-center justify-center gap-2 transition-colors"
              data-testid="close-actions-confirm">
              <Send className="h-4 w-4" />{processing ? "Processing..." : `Close ${orders.length} Orders`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CloseWithActionsModal;
